<script setup>
import {ref} from 'vue';
import {useRouter} from 'vue-router';
import {useLoginStore} from '../stores/use-login-store';
import {useAuthStore} from '@/stores/use-auth-store';
import {useOtpStore} from '@/modules/otp/stores/use-otp-store';

const showPassword = ref(false);
const showNewPassword = ref(false);
const showConfirmPassword = ref(false);

const router = useRouter();
const store = useLoginStore();
const authStore = useAuthStore();
const otpStore = useOtpStore();

async function handleCredentials() {
    const result = await store.submitCredentials();
    if (result?.done) await finishLogin(result.user);
}

async function handleOtp() {
    const result = await otpStore.verify(store.identifier);
    if (!result?.done) return;
    if (store.mustChangePassword) {
        store.step = 'set-password';
        return;
    }
    await finishLogin(store.pendingUser);
}

async function handleSetPassword() {
    const result = await store.submitNewPassword();
    if (result?.done) await finishLogin(result.user);
}

async function finishLogin(user) {
    await authStore.fetchMe().catch(() => {
        authStore.user = user;
    });
    store.reset();
    await router.replace({name: 'home'});
}
</script>

<template>
    <div class="login-wrap">
        <div class="login-box">

            <!-- Logo -->
            <div class="login-logo">
                <div class="login-logo-mark">
                    <svg
                        width="22"
                        height="22"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="#fff"
                        stroke-width="2.2"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                    >
                        <polyline points="22 12 18 12 15 21 9 3 6 12 2 12"/>
                    </svg>
                </div>
                <div>
                    <div class="login-brand">Coamana</div>
                    <div class="login-sub">Data Collection</div>
                </div>
            </div>

            <!-- Error alert (wrong credentials / general errors) -->
            <div v-if="store.error || otpStore.error" class="alert alert-error login-alert">
                <span class="alert-icon">⚠</span>
                <span>{{ store.error || otpStore.error }}</span>
            </div>

            <!-- Step: credentials -->
            <template v-if="store.step === 'credentials'">
                <div class="login-title">Good to see you</div>
                <div class="login-desc">Sign in to collect and submit market data</div>

                <div class="field">
                    <label for="login-identifier">Email or phone number</label>
                    <input
                        id="login-identifier"
                        v-model="store.identifier"
                        type="text"
                        placeholder="you@example.com or 07xx xxx xxx"
                        autocomplete="username"
                        inputmode="text"
                        :aria-invalid="!!store.validationErrors?.identifier"
                        @keyup.enter="handleCredentials"
                    />
                    <p v-if="store.validationErrors?.identifier?.[0]" class="field-error">
                        {{ store.validationErrors.identifier[0] }}
                    </p>
                </div>

                <div class="field">
                    <label for="login-pass">Password</label>
                    <div class="input-eye-wrap">
                        <input
                            id="login-pass"
                            v-model="store.password"
                            :type="showPassword ? 'text' : 'password'"
                            placeholder="••••••••"
                            autocomplete="current-password"
                            :aria-invalid="!!store.validationErrors?.password"
                            @keyup.enter="handleCredentials"
                        />
                        <button
                            type="button"
                            class="eye-btn"
                            tabindex="-1"
                            @click="showPassword = !showPassword"
                        >
                            <svg
                                v-if="!showPassword"
                                width="18"
                                height="18"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                stroke-width="2"
                                stroke-linecap="round"
                                stroke-linejoin="round"
                            >
                                <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                                <circle cx="12" cy="12" r="3"/>
                            </svg>
                            <svg
                                v-else
                                width="18"
                                height="18"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                stroke-width="2"
                                stroke-linecap="round"
                                stroke-linejoin="round"
                            >
                                <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/>
                                <line x1="1" y1="1" x2="23" y2="23"/>
                            </svg>
                        </button>
                    </div>
                    <p v-if="store.validationErrors?.password?.[0]" class="field-error">
                        {{ store.validationErrors.password[0] }}
                    </p>
                </div>

                <button
                    class="btn btn-primary"
                    :disabled="store.processing"
                    @click="handleCredentials"
                >
                    <span v-if="store.processing" class="spinner spinner-sm"></span>
                    <span>{{ store.processing ? 'Signing in…' : 'Sign in' }}</span>
                </button>

                <p class="login-hint">Forgot your password? Contact your ops admin</p>
            </template>

            <!-- Step: OTP verification -->
            <template v-else-if="store.step === 'otp'">
                <div class="login-title">Confirm this device</div>
                <div class="login-desc login-desc--otp">
                    We sent a 6-digit code
                    {{ otpStore.channel === 'sms' ? 'by text to your phone' : 'to your email' }}.
                    Enter it below.
                </div>

                <div class="otp-channels">
                    <button
                        type="button"
                        class="otp-chan"
                        :class="{ active: otpStore.channel === 'sms' }"
                        @click="otpStore.switchChannel(store.identifier, 'sms')"
                    >
                        Text my phone
                    </button>
                    <button
                        type="button"
                        class="otp-chan"
                        :class="{ active: otpStore.channel === 'email' }"
                        @click="otpStore.switchChannel(store.identifier, 'email')"
                    >
                        Email me
                    </button>
                </div>

                <div class="field">
                    <label for="login-otp">6-digit code</label>
                    <input
                        id="login-otp"
                        v-model="otpStore.code"
                        type="text"
                        inputmode="numeric"
                        autocomplete="one-time-code"
                        maxlength="6"
                        placeholder="______"
                        class="otp-input"
                        :aria-invalid="!!otpStore.validationErrors?.code"
                        @keyup.enter="handleOtp"
                    />
                    <p v-if="otpStore.validationErrors?.code?.[0]" class="field-error">
                        {{ otpStore.validationErrors.code[0] }}
                    </p>
                </div>

                <button
                    class="btn btn-primary"
                    :disabled="otpStore.processing"
                    @click="handleOtp"
                >
                    <span v-if="otpStore.processing" class="spinner spinner-sm"></span>
                    <span>{{ otpStore.processing ? 'Checking…' : 'Confirm this device' }}</span>
                </button>

                <div class="otp-foot">
                    <button
                        type="button"
                        class="btn-link"
                        @click="otpStore.resend(store.identifier)"
                    >Send a new code</button>
                    <button
                        type="button"
                        class="btn-link"
                        @click="store.backToCredentials()"
                    >Use a different account</button>
                </div>
            </template>

            <!-- Step: set password (first login / forced reset) -->
            <template v-else-if="store.step === 'set-password'">
                <div class="login-title">Set your password</div>
                <div class="login-desc">Choose a new password to continue.</div>

                <div class="field">
                    <label for="new-pass">New password</label>
                    <div class="input-eye-wrap">
                        <input
                            id="new-pass"
                            v-model="store.newPassword"
                            :type="showNewPassword ? 'text' : 'password'"
                            placeholder="At least 8 characters"
                            autocomplete="new-password"
                            :aria-invalid="!!store.validationErrors?.password"
                            @keyup.enter="handleSetPassword"
                        />
                        <button
                            type="button"
                            class="eye-btn"
                            tabindex="-1"
                            @click="showNewPassword = !showNewPassword"
                        >
                            <svg
                                v-if="!showNewPassword"
                                width="18"
                                height="18"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                stroke-width="2"
                                stroke-linecap="round"
                                stroke-linejoin="round"
                            >
                                <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                                <circle cx="12" cy="12" r="3"/>
                            </svg>
                            <svg
                                v-else
                                width="18"
                                height="18"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                stroke-width="2"
                                stroke-linecap="round"
                                stroke-linejoin="round"
                            >
                                <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/>
                                <line x1="1" y1="1" x2="23" y2="23"/>
                            </svg>
                        </button>
                    </div>
                    <p v-if="store.validationErrors?.password?.[0]" class="field-error">
                        {{ store.validationErrors.password[0] }}
                    </p>
                </div>

                <div class="field">
                    <label for="confirm-pass">Confirm password</label>
                    <div class="input-eye-wrap">
                        <input
                            id="confirm-pass"
                            v-model="store.confirmPassword"
                            :type="showConfirmPassword ? 'text' : 'password'"
                            placeholder="Repeat your password"
                            autocomplete="new-password"
                            :aria-invalid="!!store.validationErrors?.password_confirmation"
                            @keyup.enter="handleSetPassword"
                        />
                        <button
                            type="button"
                            class="eye-btn"
                            tabindex="-1"
                            @click="showConfirmPassword = !showConfirmPassword"
                        >
                            <svg
                                v-if="!showConfirmPassword"
                                width="18"
                                height="18"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                stroke-width="2"
                                stroke-linecap="round"
                                stroke-linejoin="round"
                            >
                                <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                                <circle cx="12" cy="12" r="3"/>
                            </svg>
                            <svg
                                v-else
                                width="18"
                                height="18"
                                viewBox="0 0 24 24"
                                fill="none"
                                stroke="currentColor"
                                stroke-width="2"
                                stroke-linecap="round"
                                stroke-linejoin="round"
                            >
                                <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/>
                                <line x1="1" y1="1" x2="23" y2="23"/>
                            </svg>
                        </button>
                    </div>
                    <p v-if="store.validationErrors?.password_confirmation?.[0]" class="field-error">
                        {{ store.validationErrors.password_confirmation[0] }}
                    </p>
                </div>

                <button
                    class="btn btn-primary"
                    :disabled="store.processing"
                    @click="handleSetPassword"
                >
                    <span v-if="store.processing" class="spinner spinner-sm"></span>
                    <span>{{ store.processing ? 'Saving…' : 'Set password and continue' }}</span>
                </button>
            </template>

        </div>
    </div>
</template>

<style scoped>
.login-alert { margin-bottom: 16px; }

.spinner-sm {
    width: 18px;
    height: 18px;
    border-width: 2px;
    border-top-color: #fff;
}

.login-hint {
    font-size: 12px;
    color: var(--ink4);
    text-align: center;
    margin-top: 16px;
}

.login-desc--otp { margin-bottom: 14px; }
</style>
